import moment from 'moment'
import classnames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { deserialize } from '../../../../../utility/serialization'
import { COMMON_ERRORS } from '../../../../../misc/Constants'
import { DATETIME_FORMAT, getMomentInCommunityTimezone } from '../../../../../utility/datesHelper'
import { requestCommunityMarketingAnalyticsReport } from '../../../../../actions/reportsActions'
import { displayWithCurrency } from '../../../../../utility/currency'
import { pluralize } from '../../../../../utility/helperFunctions'
import Loader from '../../../../common/loader/Loader'
import FiltersButtonDropdown, { FIXED_DATE_RANGES_FUNCTIONS } from '../../FiltersButtonDropdown'
import MarketingAnalyticsLineChart from './MarketingAnalyticsLineChart'

import businessDashboardClasses from '../BusinessDashboard.module.scss'
import classes from './MarketingAnalyticsWidget.module.scss'
import { ReactComponent as CopyIcon } from '../../../../../assets/images/common/icons/copy-icon.svg'
import { ReactComponent as OpenEyeIcon } from '../../../../../assets/images/common/icons/open-eye-icon.svg'
import { ReactComponent as EditIcon } from '../../../../../assets/images/common/icons/edit-icon.svg'
import { ReactComponent as LightBulbIcon } from '../../../../../assets/images/common/icons/light-bulb-icon.svg'

const DEFAULT_FILTERS = {
  ...FIXED_DATE_RANGES_FUNCTIONS['thisYear'].valueFunc(),
  categoryOption: null,
  programOption: null,
}

const MarketingAnalyticsWidget = () => {
  const community = useSelector(state => state.user.current_user.communities[0])
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState(null)

  const landingPageUrl = `${window.location.origin}/landing/${community?.id}`
  const openLeads = data?.statistics?.openLeadsCount
  const avgRevenue = data?.statistics?.avgEnrollmentRevenueTtm
  const estimatedRevenue = avgRevenue !== null ? displayWithCurrency(openLeads * avgRevenue, community?.currency, true) : null

  useEffect(() => {
    setIsLoading(true)

    requestCommunityMarketingAnalyticsReport({
      communityId: community?.id,
      startDate: getMomentInCommunityTimezone(moment(filters.startDate), community?.timezone, DATETIME_FORMAT)
        .toDate(),
      endDate: getMomentInCommunityTimezone(moment(filters.endDate), community?.timezone, DATETIME_FORMAT)
        .toDate(),
    })
      .then(res => {
        setData(deserialize(res.data.report.data))
        setIsLoading(false)
      })
      .catch(() => toast.error(COMMON_ERRORS[500]))
  }, [community, filters.startDate, filters.endDate])

  const chartLabels = useMemo(() => {
    if (!data?.chartData) {
      return []
    }
    const currentYear = new Date().getFullYear()
    // The API doesn't flag granularity, so infer it: daily snapshots are one day
    // apart, monthly ones are >= 28. Daily ranges show 'Apr 15' instead of 'Apr'.
    const isDaily = data.chartData.length > 1 &&
      moment.utc(data.chartData[1].date).diff(moment.utc(data.chartData[0].date), 'days') < 28
    return data.chartData.map(item => {
      const date = moment.utc(item.date)
      if (isDaily) {
        return date.year() !== currentYear ? date.format('MMM D, YYYY') : date.format('MMM D')
      }
      return date.year() !== currentYear ? date.format('MMM YYYY') : date.format('MMM')
    })
  }, [data?.chartData])

  const handleSubmitFilters = (newFilters) => {
    setFilters(prevState => ({ ...prevState, ...newFilters }))
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(landingPageUrl)
      .then(() => {
        toast.success('Link was copied to clipboard')
      })
  }

  return (
    <section className={businessDashboardClasses.widget}>
      <div className={classnames('d-flex justify-content-between', businessDashboardClasses.titleWrapper)}>
        <h2 className={businessDashboardClasses.title}>Marketing Analytics</h2>
        <FiltersButtonDropdown
          defaultFilters={DEFAULT_FILTERS}
          appliedFilters={filters}
          onSubmitFilters={handleSubmitFilters}
          dateOnly
        />
      </div>

      <div className='d-flex justify-content-between'>
        <button
          className={classes.quickActionButton}
          onClick={handleCopyLink}
        >
          <CopyIcon className={classes.icon} />
          <span>Copy Landing Page Link</span>
        </button>
        <Link
          to={`/landing/${community?.id}`}
          target='_blank'
          className={classes.quickActionButton}
        >
          <OpenEyeIcon className={classes.icon} />
          <span>View Landing Page</span>
        </Link>
        <Link
          to='/leads'
          target='_blank'
          className={classes.quickActionButton}
        >
          <OpenEyeIcon className={classes.icon} />
          <span>View Leads</span>
        </Link>
        <Link
          to='/community-settings/landing'
          target='_blank'
          className={classes.quickActionButton}
        >
          <EditIcon className={classes.icon} />
          <span>Edit Landing Page</span>
        </Link>
      </div>

      {isLoading ? (
        <div className='flex-grow-1 d-flex justify-content-center align-items-center py-5'>
          <Loader>Loading data</Loader>
        </div>
      ) : (
        <>
          <div className={classes.chartsWrapper}>
            <MarketingAnalyticsLineChart
              chartKey='landingPageViews'
              labels={chartLabels}
              data={data?.chartData?.map(item => item.landingPageViews) ?? []}
              statistics={data?.statistics?.landingPageViews}
            />
            <MarketingAnalyticsLineChart
              chartKey='leadsGenerated'
              labels={chartLabels}
              data={data?.chartData?.map(item => item.leadsGenerated) ?? []}
              statistics={data?.statistics?.leadsGenerated}
            />
            <MarketingAnalyticsLineChart
              chartKey='registrations'
              labels={chartLabels}
              data={data?.chartData?.map(item => item.registrations) ?? []}
              statistics={data?.statistics?.registrations}
            />
          </div>

          {openLeads > 0 && (
            <div className={classnames('mb-0 d-flex', businessDashboardClasses.sectionWrapper)}>
              <LightBulbIcon className={classnames('fa fa-lightbulb-o mr-4', classes.leadsInsightIcon)} />
              <div>
                <h3 className={businessDashboardClasses.title}>Lead Insights</h3>
                <p className={classes.leadsInsightText}>
                  You currently have {openLeads} open {pluralize('lead', openLeads)}
                  {estimatedRevenue !== null && <>, representing a {estimatedRevenue} opportunity</>}.
                  <br />
                  Follow up with these golfers to turn your marketing activity into measurable growths.
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  )
}

export default MarketingAnalyticsWidget
